/**
 * Geometric Constraint Solver - BIM 2025 Grade
 * 
 * Iterative relaxation solver for 2D sketch constraints and 3D alignment locks.
 * Uses a impulse-based position correction approach (similar to Verlet integration or weak constraints).
 */

import { v4 as uuidv4 } from 'uuid';

export type ConstraintType =
    | 'Coincident'      // Points must be at same location
    | 'CoincidentPlane' // Point must be on a plane (3D alignment)
    | 'CoincidentLine'  // Point must be on a line (3D alignment)
    | 'Parallel'        // Lines must have same direction vector
    | 'Perpendicular'   // Lines must have dot product of 0
    | 'Horizontal'      // Line must be horizontal
    | 'Vertical'        // Line must be vertical
    | 'Distance'        // Points/Lines must be at fixed distance
    | 'DistanceX'       // Fixed horizontal offset between two points (Faz 6)
    | 'DistanceY'       // Fixed vertical offset between two points (Faz 6)
    | 'Angle'           // Lines must be at fixed angle
    | 'Concentric'      // Arc/Circle centers must be coincident
    | 'Tangent'         // Line/Arc must touch at one point
    | 'Midpoint'        // Point pinned to the midpoint of a segment (Faz 6)
    | 'PointOnCurve'    // Point pinned onto a curve (line/circle) (Faz 6)
    | 'Colinear'        // Segment endpoints on an infinite line (Faz 6)
    | 'Symmetric'       // Two points mirrored across an axis line (Faz 6)
    | 'Equality';       // Dimensions must be equal

export interface SolverPoint {
    id: string; // "elementId:start" or "elementId:end"
    x: number;
    y: number;
    z: number;
    pinned: boolean; // If true, mass is infinite (invMass = 0)
}

// Internal solver use only
interface SolverParticle {
    id: string;
    x: number;
    y: number;
    z: number;
    oldX: number;
    oldY: number;
    oldZ: number;
    invMass: number;
}

export interface SolverConstraint {
    id: string;
    type: ConstraintType;
    references: string[]; // particle IDs usually
    values: number[]; // [distance, angle, etc.]
    stiffness: number; // 0..1 (1 = rigid)
}

export class GeometricConstraintSolver {
    private particles: Map<string, SolverParticle> = new Map();
    private constraints: SolverConstraint[] = [];
    /** OCS parity: relaxation runs at most 50 iterations per subsystem. */
    private iterations: number = 50;
    /** Report of the last solve() call (iterations + total violation). */
    lastReport: { iterations: number; residual: number } = { iterations: 0, residual: 0 };
    /**
     * Rigid companions (Faz 6): a radius handle follows its center's
     * per-iteration TRANSLATION delta, so moving a circle never silently
     * changes its radius while Radius/Distance constraints can still move
     * the handle radially.
     */
    private companions: Array<{ follower: string; leader: string; prevX: number; prevY: number; prevZ: number }> = [];

    /**
     * Initialize/Reset the solver with current geometry state
     */
    clear(): void {
        this.particles.clear();
        this.constraints = [];
    }

    /**
     * Register a point in the solver
     */
    addPoint(id: string, x: number, y: number, z: number = 0, pinned: boolean = false): void {
        this.particles.set(id, {
            id,
            x, y, z,
            oldX: x, oldY: y, oldZ: z,
            invMass: pinned ? 0 : 1
        });
    }

    /**
     * Update point position (e.g. from user dragging) before solving
     */
    updatePoint(id: string, x: number, y: number, z: number = 0): void {
        const p = this.particles.get(id);
        if (p && p.invMass > 0) { // Don't move pinned points via update (unless pinned state changes)
            p.x = x;
            p.y = y;
            p.z = z;
        }
    }

    /**
     * Add a constraint definition
     */
    addConstraint(type: ConstraintType, references: string[], values: number[] = [0]): string {
        const id = uuidv4();
        // Check availability
        for (const ref of references) {
            if (!this.particles.has(ref)) {
                console.warn(`Constraint references missing particle: ${ref}`);
                return id; // Fail gracefully
            }
        }

        this.constraints.push({
            id,
            type,
            references,
            values,
            stiffness: 1.0
        });
        return id;
    }

    /**
     * Add a constraint carrying the caller's stable id (Faz 6 driver: the
     * id maps solver failures back to ConstraintRecords for the conflict
     * report).
     */
    addConstraintWithId(id: string, type: ConstraintType, references: string[], values: number[] = []): void {
        for (const ref of references) {
            if (!this.particles.has(ref)) return; // Fail silently like addConstraint
        }
        this.constraints.push({
            id,
            type,
            references,
            values,
            stiffness: 1.0
        });
    }

    /**
     * Solve constraints. Returns true when fully converged.
     * Convergence contract (Faz 6.3, OCS parity): run at most `iterations`
     * passes and stop early once the TOTAL violation drops below
     * 1e-6 * scale, where scale is the diagonal of the particle cloud.
     */
    solve(): boolean {
        return this.solveDetailed().converged;
    }

    /**
     * Solve with reporting. `residual` is the summed constraint violation at
     * exit; callers report non-converged subsystems as conflicts instead of
     * throwing (OCS conflicts cache parity).
     */
    solveDetailed(): { converged: boolean; iterations: number; residual: number } {
        const scale = this.characteristicScale();
        const threshold = 1e-6 * scale;
        let totalError = 0;
        let used = 0;
        for (let i = 0; i < this.iterations; i++) {
            // Snapshot companion leaders, solve, then replay their translation
            // delta onto the followers (rigid center->radius-handle follow).
            for (const comp of this.companions) {
                const leader = this.particles.get(comp.leader);
                if (!leader) continue;
                comp.prevX = leader.x;
                comp.prevY = leader.y;
                comp.prevZ = leader.z;
            }
            totalError = 0;
            for (const c of this.constraints) {
                totalError += this.solveConstraint(c);
            }
            for (const comp of this.companions) {
                const leader = this.particles.get(comp.leader);
                const follower = this.particles.get(comp.follower);
                if (!leader || !follower || follower.invMass <= 0) continue;
                follower.x += leader.x - comp.prevX;
                follower.y += leader.y - comp.prevY;
                follower.z += leader.z - comp.prevZ;
            }
            used = i + 1;
            if (totalError < threshold) break;
        }
        this.lastReport = { iterations: used, residual: totalError };
        return { converged: totalError < threshold, iterations: used, residual: totalError };
    }

    /** Bind a follower particle to mirror its leader's translation deltas. */
    bindCompanion(follower: string, leader: string): void {
        if (follower === leader) return;
        if (this.companions.some((c) => c.follower === follower)) return;
        this.companions.push({ follower, leader, prevX: 0, prevY: 0, prevZ: 0 });
    }

    /** Characteristic drawing size: diagonal of the particle bounding box. */
    private characteristicScale(): number {
        if (this.particles.size === 0) return 1;
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        this.particles.forEach((p) => {
            if (p.x < minX) minX = p.x;
            if (p.y < minY) minY = p.y;
            if (p.x > maxX) maxX = p.x;
            if (p.y > maxY) maxY = p.y;
        });
        const diag = Math.hypot(maxX - minX, maxY - minY);
        return Number.isFinite(diag) && diag > 1e-9 ? diag : 1;
    }

    /**
     * Get current positions after solve
     */
    getPositions(): Map<string, { x: number, y: number, z: number }> {
        const res = new Map();
        this.particles.forEach((p, id) => {
            res.set(id, { x: p.x, y: p.y, z: p.z });
        });
        return res;
    }

    // ================== Individual Constraint Solvers ==================

    private solveConstraint(c: SolverConstraint): number {
        switch (c.type) {
            case 'Coincident': return this.solveCoincident(c);
            case 'CoincidentPlane': return this.solveCoincidentPlane(c);
            case 'CoincidentLine': return this.solveCoincidentLine(c);
            case 'Distance': return this.solveDistance(c);
            case 'DistanceX': return this.solveDistanceAxis(c, 'x');
            case 'DistanceY': return this.solveDistanceAxis(c, 'y');
            case 'Horizontal': return this.solveHorizontal(c);
            case 'Vertical': return this.solveVertical(c);
            case 'Parallel': return this.solveParallel(c);
            case 'Perpendicular': return this.solvePerpendicular(c);
            case 'Angle': return this.solveAngle(c);
            case 'Tangent': return this.solveTangent(c);
            case 'Concentric': return this.solveConcentric(c);
            case 'Midpoint': return this.solveMidpoint(c);
            case 'PointOnCurve': return this.solvePointOnCurve(c);
            case 'Colinear': return this.solveColinear(c);
            case 'Symmetric': return this.solveSymmetric(c);
            case 'Equality': return this.solveEquality(c);
        }
        return 0;
    }

    /**
     * Fixed axis offset between two points (DistanceX / DistanceY). Keeps
     * the sign of the current offset and scales its magnitude to the target.
     */
    private solveDistanceAxis(c: SolverConstraint, axis: 'x' | 'y'): number {
        const p1 = this.particles.get(c.references[0])!;
        const p2 = this.particles.get(c.references[1])!;
        const target = Math.abs(c.values[0]);
        const idx = axis === 'x' ? 0 : 1;
        const d = (axis === 'x' ? p2.x - p1.x : p2.y - p1.y);
        const sign = d >= 0 ? 1 : -1;
        const err = Math.abs(d) - target;
        if (Math.abs(err) < 1e-12) return 0;
        const correction = -err * sign * c.stiffness;
        const totalInv = p1.invMass + p2.invMass;
        if (totalInv <= 0) return Math.abs(err);
        const w1 = p1.invMass / totalInv;
        const w2 = p2.invMass / totalInv;
        if (axis === 'x') {
            p1.x -= correction * w1;
            p2.x += correction * w2;
        } else {
            p1.y -= correction * w1;
            p2.y += correction * w2;
        }
        return Math.abs(err);
    }

    /**
     * Fixed angle between two lines (refs: p1-p2, p3-p4; values[0] radians).
     * Line direction is sign-agnostic, so π-shifted targets collapse onto
     * the nearer branch — matching AutoCAD's unsigned angular measure.
     */
    private solveAngle(c: SolverConstraint): number {
        const p1 = this.particles.get(c.references[0])!;
        const p2 = this.particles.get(c.references[1])!;
        const p3 = this.particles.get(c.references[2])!;
        const p4 = this.particles.get(c.references[3])!;

        const u = { x: p2.x - p1.x, y: p2.y - p1.y };
        const v = { x: p4.x - p3.x, y: p4.y - p3.y };
        if (Math.hypot(u.x, u.y) < 1e-9 || Math.hypot(v.x, v.y) < 1e-9) return 0;

        const dAngle = normalizeAngle(Math.atan2(v.y, v.x) - Math.atan2(u.y, u.x));
        const target = c.values[0] ?? 0;
        let err = normalizeAngle(dAngle - target);
        const errAlt = normalizeAngle(dAngle - (target + Math.PI));
        if (Math.abs(errAlt) < Math.abs(err)) err = errAlt;
        if (Math.abs(err) < 1e-12) return 0;

        const half = (err / 2) * c.stiffness;
        this.rotateSegment(p1, p2, half);
        this.rotateSegment(p3, p4, -half);
        return Math.abs(err);
    }

    /**
     * Tangency (Faz 6): two encodings share the type —
     *   line + circle: refs [p1, p2, c, rp], values[1] = 0
     *     center-line distance must equal the radius; the center and the
     *     line slide along the shared normal.
     *   circle + circle: refs [c1, rp1, c2, rp2], values[1] = 1
     *     center distance must equal r1+r2 (external) or |r1-r2| (internal),
     *     whichever the current geometry is nearer to.
     */
    private solveTangent(c: SolverConstraint): number {
        if (c.values[1] === 1) {
            const c1 = this.particles.get(c.references[0])!;
            const c2 = this.particles.get(c.references[2])!;
            const r1 = this.radiusOf(c.references[0], c.references[1]);
            const r2 = this.radiusOf(c.references[2], c.references[3]);
            if (r1 === null || r2 === null) return 0;

            const dx = c2.x - c1.x, dy = c2.y - c1.y;
            const d = Math.hypot(dx, dy);
            if (d < 1e-9) return 0;
            const outErr = d - (r1 + r2);
            const inErr = d - Math.abs(r1 - r2);
            const err = Math.abs(outErr) <= Math.abs(inErr) ? outErr : inErr;
            if (Math.abs(err) < 1e-12) return 0;

            const nx = dx / d, ny = dy / d;
            const totalInv = c1.invMass + c2.invMass;
            if (totalInv <= 0) return Math.abs(err);
            // Centers slide along the center line; radii stay untouched.
            c1.x += nx * err * c.stiffness * (c1.invMass / totalInv);
            c1.y += ny * err * c.stiffness * (c1.invMass / totalInv);
            c2.x -= nx * err * c.stiffness * (c2.invMass / totalInv);
            c2.y -= ny * err * c.stiffness * (c2.invMass / totalInv);
            return Math.abs(err);
        }

        // Line + circle: measure the signed distance from the segment
        // MIDPOINT (both endpoints move the line), then distribute the
        // correction by inverse mass over the squared gradient magnitudes
        // (center |grad| = 1, endpoints |grad| = 1/2) so the violation
        // closes fully in one projection.
        const p1 = this.particles.get(c.references[0])!;
        const p2 = this.particles.get(c.references[1])!;
        const c0 = this.particles.get(c.references[2])!;
        const rp = this.particles.get(c.references[3])!;
        const r = Math.hypot(rp.x - c0.x, rp.y - c0.y);

        const ux = p2.x - p1.x, uy = p2.y - p1.y;
        const lenU = Math.hypot(ux, uy);
        if (lenU < 1e-9) return 0;
        const nx = -uy / lenU, ny = ux / lenU; // unit normal of the line
        const baseX = (p1.x + p2.x) / 2, baseY = (p1.y + p2.y) / 2;
        const dSigned = (c0.x - baseX) * nx + (c0.y - baseY) * ny;
        const side = dSigned >= 0 ? 1 : -1;
        const err = Math.abs(dSigned) - r;
        if (Math.abs(err) < 1e-12) return 0;

        const wC = c0.invMass;
        const w1 = p1.invMass, w2 = p2.invMass;
        const denom = wC * 1 + w1 * 0.25 + w2 * 0.25;
        if (denom <= 0) return Math.abs(err);
        const step = err * c.stiffness;
        if (wC > 0) {
            c0.x -= side * nx * step * (wC / denom);
            c0.y -= side * ny * step * (wC / denom);
        }
        if (w1 > 0) {
            p1.x += side * nx * step * ((w1 * 0.25) / denom);
            p1.y += side * ny * step * ((w1 * 0.25) / denom);
        }
        if (w2 > 0) {
            p2.x += side * nx * step * ((w2 * 0.25) / denom);
            p2.y += side * ny * step * ((w2 * 0.25) / denom);
        }
        return Math.abs(err);
    }

    /** Current radius from a center + radius-handle particle pair. */
    private radiusOf(centerId: string, radiusId: string): number | null {
        const c = this.particles.get(centerId);
        const rp = this.particles.get(radiusId);
        if (!c || !rp) return null;
        return Math.hypot(rp.x - c.x, rp.y - c.y);
    }

    /** Concentric = coincident centers (OCS: solved as Coincident). */
    private solveConcentric(c: SolverConstraint): number {
        return this.solveCoincident(c);
    }

    /** Pin a point to the midpoint of segment (p1,p2). */
    private solveMidpoint(c: SolverConstraint): number {
        const p0 = this.particles.get(c.references[0])!;
        const p1 = this.particles.get(c.references[1])!;
        const p2 = this.particles.get(c.references[2])!;
        const midX = (p1.x + p2.x) / 2;
        const midY = (p1.y + p2.y) / 2;
        const dx = midX - p0.x, dy = midY - p0.y;
        const err = Math.hypot(dx, dy);
        if (err < 1e-12) return 0;
        if (p0.invMass > 0) {
            p0.x += dx * c.stiffness;
            p0.y += dy * c.stiffness;
        }
        return err;
    }

    /**
     * Point on curve (Faz 6): the driver flattens the curve into refs —
     *   line/segment: [p0, a, b]  -> project onto the infinite line
     *   circle/arc:   [p0, c, rp] -> project onto the circle of radius r
     */
    private solvePointOnCurve(c: SolverConstraint): number {
        const p0 = this.particles.get(c.references[0])!;
        if (c.values[0] === 1) {
            // Circular curve: pull the point onto the radius.
            const c0 = this.particles.get(c.references[1])!;
            const rp = this.particles.get(c.references[2])!;
            const r = Math.hypot(rp.x - c0.x, rp.y - c0.y);
            const dx = p0.x - c0.x, dy = p0.y - c0.y;
            const d = Math.hypot(dx, dy);
            if (d < 1e-9) return 0;
            const err = Math.abs(d - r);
            if (err < 1e-12) return 0;
            if (p0.invMass > 0) {
                const k = ((r - d) / d) * c.stiffness;
                p0.x += dx * k;
                p0.y += dy * k;
            }
            return err;
        }
        // Straight curve: project onto the infinite line through a-b.
        const a = this.particles.get(c.references[1])!;
        const b = this.particles.get(c.references[2])!;
        const ux = b.x - a.x, uy = b.y - a.y;
        const lenU = Math.hypot(ux, uy);
        if (lenU < 1e-9) return 0;
        const nx = -uy / lenU, ny = ux / lenU;
        const dist = (p0.x - a.x) * nx + (p0.y - a.y) * ny;
        const err = Math.abs(dist);
        if (err < 1e-12) return 0;
        if (p0.invMass > 0) {
            p0.x -= nx * dist * c.stiffness;
            p0.y -= ny * dist * c.stiffness;
        }
        return err;
    }

    /** Both endpoints of segment c-d lie on the infinite line through a-b. */
    private solveColinear(c: SolverConstraint): number {
        const a = this.particles.get(c.references[0])!;
        const b = this.particles.get(c.references[1])!;
        const p = this.particles.get(c.references[2])!;
        const q = this.particles.get(c.references[3])!;
        const ux = b.x - a.x, uy = b.y - a.y;
        const lenU = Math.hypot(ux, uy);
        if (lenU < 1e-9) return 0;
        const nx = -uy / lenU, ny = ux / lenU;
        let err = 0;
        for (const pnt of [p, q]) {
            const dist = (pnt.x - a.x) * nx + (pnt.y - a.y) * ny;
            err += Math.abs(dist);
            if (Math.abs(dist) > 1e-12 && pnt.invMass > 0) {
                pnt.x -= nx * dist * c.stiffness;
                pnt.y -= ny * dist * c.stiffness;
            }
        }
        return err;
    }

    /**
     * Two points mirrored across an axis line (refs: p1, p2, a1, a2):
     * pull the pair's midpoint onto the axis and remove the axis-parallel
     * component of the connector — the two conditions of point symmetry.
     */
    private solveSymmetric(c: SolverConstraint): number {
        const p1 = this.particles.get(c.references[0])!;
        const p2 = this.particles.get(c.references[1])!;
        const a1 = this.particles.get(c.references[2])!;
        const a2 = this.particles.get(c.references[3])!;
        const ux = a2.x - a1.x, uy = a2.y - a1.y;
        const lenU = Math.hypot(ux, uy);
        if (lenU < 1e-9) return 0;
        const uX = ux / lenU, uY = uy / lenU;

        let err = 0;
        const wT = p1.invMass + p2.invMass;
        if (wT > 0) {
            const midX = (p1.x + p2.x) / 2, midY = (p1.y + p2.y) / 2;
            const t = (midX - a1.x) * uX + (midY - a1.y) * uY;
            const projX = a1.x + t * uX, projY = a1.y + t * uY;
            const offX = projX - midX, offY = projY - midY;
            err += Math.hypot(offX, offY);
            if (err > 1e-12) {
                p1.x += offX * c.stiffness * (p1.invMass / wT);
                p1.y += offY * c.stiffness * (p1.invMass / wT);
                p2.x += offX * c.stiffness * (p2.invMass / wT);
                p2.y += offY * c.stiffness * (p2.invMass / wT);
            }
            // Connector must be perpendicular to the axis.
            const vX = p2.x - p1.x, vY = p2.y - p1.y;
            const s = vX * uX + vY * uY;
            err += Math.abs(s);
            if (Math.abs(s) > 1e-12) {
                const halfX = (s / 2) * c.stiffness * uX;
                const halfY = (s / 2) * c.stiffness * uY;
                p1.x += halfX * (p1.invMass / wT);
                p1.y += halfY * (p1.invMass / wT);
                p2.x -= halfX * (p2.invMass / wT);
                p2.y -= halfY * (p2.invMass / wT);
            }
        }
        return err;
    }

    /**
     * Equality (Faz 6): two lines share a length (refs p1,p2,p3,p4) or two
     * circles share a radius (refs c1,rp1,c2,rp2). Both move to the mean.
     */
    private solveEquality(c: SolverConstraint): number {
        if (c.values[0] === 1) {
            // Circle radius equality
            const c1 = this.particles.get(c.references[0])!;
            const rp1 = this.particles.get(c.references[1])!;
            const c2 = this.particles.get(c.references[2])!;
            const rp2 = this.particles.get(c.references[3])!;
            const r1 = Math.hypot(rp1.x - c1.x, rp1.y - c1.y);
            const r2 = Math.hypot(rp2.x - c2.x, rp2.y - c2.y);
            const avg = (r1 + r2) / 2;
            const err = Math.abs(r1 - r2);
            if (err < 1e-12) return 0;
            this.projectRadius(c1, rp1, r1, avg, c.stiffness);
            this.projectRadius(c2, rp2, r2, avg, c.stiffness);
            return err;
        }
        // Line length equality
        const p1 = this.particles.get(c.references[0])!;
        const p2 = this.particles.get(c.references[1])!;
        const p3 = this.particles.get(c.references[2])!;
        const p4 = this.particles.get(c.references[3])!;
        const l1 = Math.hypot(p2.x - p1.x, p2.y - p1.y);
        const l2 = Math.hypot(p4.x - p3.x, p4.y - p3.y);
        const avg = (l1 + l2) / 2;
        const err = Math.abs(l1 - l2);
        if (err < 1e-12) return 0;
        this.projectSegmentLength(p1, p2, l1, avg, c.stiffness);
        this.projectSegmentLength(p3, p4, l2, avg, c.stiffness);
        return err;
    }

    /** Slide one segment endpoint pair toward a target length. */
    private projectSegmentLength(
        pa: { x: number; y: number; invMass: number },
        pb: { x: number; y: number; invMass: number },
        current: number,
        target: number,
        stiffness: number
    ): void {
        if (current < 1e-9) return;
        const delta = target - current;
        const nx = (pb.x - pa.x) / current;
        const ny = (pb.y - pa.y) / current;
        const totalInv = pa.invMass + pb.invMass;
        if (totalInv <= 0) return;
        const corr = delta * stiffness;
        if (pa.invMass > 0) { pa.x -= nx * corr * (pa.invMass / totalInv); pa.y -= ny * corr * (pa.invMass / totalInv); }
        if (pb.invMass > 0) { pb.x += nx * corr * (pb.invMass / totalInv); pb.y += ny * corr * (pb.invMass / totalInv); }
    }

    /** Move a radius handle radially so the radius hits the target. */
    private projectRadius(
        c: { x: number; y: number },
        rp: { x: number; y: number; invMass: number },
        current: number,
        target: number,
        stiffness: number
    ): void {
        if (current < 1e-9 || rp.invMass <= 0) return;
        const k = ((target - current) / current) * stiffness;
        rp.x += (rp.x - c.x) * k;
        rp.y += (rp.y - c.y) * k;
    }

    private solveCoincident(c: SolverConstraint): number {
        // Ref 0 and Ref 1 should be at same pos
        const p1 = this.particles.get(c.references[0])!;
        const p2 = this.particles.get(c.references[1])!;

        const dx = p1.x - p2.x;
        const dy = p1.y - p2.y;
        const dz = p1.z - p2.z;

        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (dist < 0.0001) return 0;

        const correction = (dist * c.stiffness) / (p1.invMass + p2.invMass);
        const nx = dx / dist;
        const ny = dy / dist;
        const nz = dz / dist;

        if (p1.invMass > 0) {
            p1.x -= nx * correction * p1.invMass;
            p1.y -= ny * correction * p1.invMass;
            p1.z -= nz * correction * p1.invMass;
        }
        if (p2.invMass > 0) {
            p2.x += nx * correction * p2.invMass;
            p2.y += ny * correction * p2.invMass;
            p2.z += nz * correction * p2.invMass;
        }

        return dist;
    }

    private solveDistance(c: SolverConstraint): number {
        const p1 = this.particles.get(c.references[0])!;
        const p2 = this.particles.get(c.references[1])!;
        const targetLen = c.values[0];

        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        const dz = p2.z - p1.z;

        const currentLen = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (currentLen === 0) return targetLen; // Degenerate, can't solve direction

        const delta = currentLen - targetLen;
        const error = Math.abs(delta);

        const correction = (delta * c.stiffness) / (p1.invMass + p2.invMass);
        const nx = dx / currentLen;
        const ny = dy / currentLen;
        const nz = dz / currentLen;

        if (p1.invMass > 0) {
            p1.x += nx * correction * p1.invMass;
            p1.y += ny * correction * p1.invMass;
            p1.z += nz * correction * p1.invMass;
        }
        if (p2.invMass > 0) {
            p2.x -= nx * correction * p2.invMass;
            p2.y -= ny * correction * p2.invMass;
            p2.z -= nz * correction * p2.invMass;
        }

        return error;
    }

    private solveHorizontal(c: SolverConstraint): number {
        // p1 and p2 should have dy = 0
        const p1 = this.particles.get(c.references[0])!;
        const p2 = this.particles.get(c.references[1])!;

        const dy = p2.y - p1.y; // Assuming Z is up, Horizontal is on XY plane? 
        // Logic: Horizontal usually means Parallel to X axis
        const error = Math.abs(dy);

        const correction = (dy * c.stiffness) / (p1.invMass + p2.invMass);

        if (p1.invMass > 0) p1.y += correction * p1.invMass;
        if (p2.invMass > 0) p2.y -= correction * p2.invMass;

        return error;
    }

    private solveVertical(c: SolverConstraint): number {
        // Vertical in 2D View usually means Parallel to Y axis (dx = 0)
        const p1 = this.particles.get(c.references[0])!;
        const p2 = this.particles.get(c.references[1])!;

        const dx = p2.x - p1.x;
        const error = Math.abs(dx);
        const correction = (dx * c.stiffness) / (p1.invMass + p2.invMass);

        if (p1.invMass > 0) p1.x += correction * p1.invMass;
        if (p2.invMass > 0) p2.x -= correction * p2.invMass;

        return error;
    }

    private solveCoincidentPlane(c: SolverConstraint): number {
        // Ref 0 is point, Ref 1,2,3 define plane OR Ref 1 is a "Plane Object" (not supported by particle system directly yet)
        // Alternative: c.values = [nx, ny, nz, d] for a fixed plane
        // Let's assume fixed plane for "Lock to Level" / "Lock to Grid"
        // references[0] = point ID
        // values = [nx, ny, nz, d] where plane equation is nx*x + ny*y + nz*z + d = 0

        const p = this.particles.get(c.references[0])!;
        const nx = c.values[0];
        const ny = c.values[1];
        const nz = c.values[2];
        const d = c.values[3];

        // Signed distance to plane
        const dist = nx * p.x + ny * p.y + nz * p.z + d;
        const error = Math.abs(dist);

        if (error < 0.0001) return 0;

        const correction = -dist * c.stiffness; // Project point onto plane

        if (p.invMass > 0) {
            p.x += nx * correction * p.invMass;
            p.y += ny * correction * p.invMass;
            p.z += nz * correction * p.invMass;
        }

        return error;
    }

    private solveCoincidentLine(c: SolverConstraint): number {
        // Point constrained to a fixed 3D line (e.g. Reference Line)
        // references[0] = point
        // values = [p0x, p0y, p0z, dx, dy, dz] (Point on line + direction)

        const p = this.particles.get(c.references[0])!;
        const l0 = { x: c.values[0], y: c.values[1], z: c.values[2] };
        const dir = { x: c.values[3], y: c.values[4], z: c.values[5] };

        // Vector from line origin to particle
        const v = { x: p.x - l0.x, y: p.y - l0.y, z: p.z - l0.z };

        // Project v onto dir
        const t = v.x * dir.x + v.y * dir.y + v.z * dir.z;

        // Closest point on line
        const closest = {
            x: l0.x + t * dir.x,
            y: l0.y + t * dir.y,
            z: l0.z + t * dir.z
        };

        const dx = p.x - closest.x;
        const dy = p.y - closest.y;
        const dz = p.z - closest.z;

        const error = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (error < 0.0001) return 0;

        const correction = c.stiffness;

        if (p.invMass > 0) {
            p.x -= dx * correction * p.invMass;
            p.y -= dy * correction * p.invMass;
            p.z -= dz * correction * p.invMass;
        }

        return error;
    }

    private solveParallel(c: SolverConstraint): number {
        // Line1(p1-p2) || Line2(p3-p4)
        // Cross product should be 0 (in 2D z-component of cross product)
        // (x2-x1)*(y4-y3) - (y2-y1)*(x4-x3) = 0

        const p1 = this.particles.get(c.references[0])!;
        const p2 = this.particles.get(c.references[1])!;
        const p3 = this.particles.get(c.references[2])!;
        const p4 = this.particles.get(c.references[3])!;

        const u = { x: p2.x - p1.x, y: p2.y - p1.y };
        const v = { x: p4.x - p3.x, y: p4.y - p3.y };

        // Normalize? It helps stability
        const lenU = Math.sqrt(u.x * u.x + u.y * u.y);
        const lenV = Math.sqrt(v.x * v.x + v.y * v.y);
        if (lenU < 0.001 || lenV < 0.001) return 0;

        const nx = u.x / lenU;
        const ny = u.y / lenU;
        const mx = v.x / lenV;
        const my = v.y / lenV;

        // Cross product value = sin(theta)
        const cross = nx * my - ny * mx;
        const error = Math.abs(cross); // We want this to be 0

        // Angular correction: rotate both segments halfway toward the shared
        // average direction, weighted by particle mobility (PBD style).
        const dAngle = normalizeAngle(Math.atan2(v.y, v.x) - Math.atan2(u.y, u.x));
        const half = (dAngle / 2) * c.stiffness;
        this.rotateSegment(p1, p2, half);
        this.rotateSegment(p3, p4, -half);

        return error;
    }

    private solvePerpendicular(c: SolverConstraint): number {
        // Line1(p1-p2) ⊥ Line2(p3-p4): dot product = 0
        const p1 = this.particles.get(c.references[0])!;
        const p2 = this.particles.get(c.references[1])!;
        const p3 = this.particles.get(c.references[2])!;
        const p4 = this.particles.get(c.references[3])!;

        const u = { x: p2.x - p1.x, y: p2.y - p1.y };
        const v = { x: p4.x - p3.x, y: p4.y - p3.y };

        const lenU = Math.hypot(u.x, u.y);
        const lenV = Math.hypot(v.x, v.y);
        if (lenU < 0.001 || lenV < 0.001) return 0;

        const dAngle = normalizeAngle(Math.atan2(v.y, v.x) - Math.atan2(u.y, u.x));
        // Pick the perpendicular target (±π/2) nearest the current angle.
        const ePlus = normalizeAngle(dAngle - Math.PI / 2);
        const eMinus = normalizeAngle(dAngle + Math.PI / 2);
        const err = Math.abs(ePlus) <= Math.abs(eMinus) ? ePlus : eMinus;
        const error = Math.abs(Math.cos(dAngle)); // normalized dot = deviation from ⊥

        const half = (err / 2) * c.stiffness;
        this.rotateSegment(p1, p2, half);
        this.rotateSegment(p3, p4, -half);

        return error;
    }

    /**
     * Rotate segment a→b by `angle` around its midpoint, distributing the
     * rotation between the two endpoints by inverse mass so pinned particles
     * stay put.
     */
    private rotateSegment(pa: { x: number; y: number; invMass: number }, pb: { x: number; y: number; invMass: number }, angle: number): void {
        if (angle === 0) return;
        const totalInv = pa.invMass + pb.invMass;
        if (totalInv <= 0) return;
        this.rotatePointAbout(pa, pb, angle * (pa.invMass / totalInv));
        this.rotatePointAbout(pb, pa, angle * (pb.invMass / totalInv));
    }

    /** Rotate `p` about `pivot` by `angle` (in place). */
    private rotatePointAbout(p: { x: number; y: number }, pivot: { x: number; y: number }, angle: number): void {
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        const dx = p.x - pivot.x;
        const dy = p.y - pivot.y;
        p.x = pivot.x + dx * cos - dy * sin;
        p.y = pivot.y + dx * sin + dy * cos;
    }
}

/** Wrap an angle difference into (−π, π]. */
function normalizeAngle(a: number): number {
    while (a > Math.PI) a -= 2 * Math.PI;
    while (a <= -Math.PI) a += 2 * Math.PI;
    return a;
}
